import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Table } from '@medusajs/ui';

import { DEFAULT_COUNTRY_CODE } from '@lib/constants/country-code';
import { ordersQueryOptions } from '@lib/query/options/orders';
import { fulfillmentStatusMap } from '@lib/util/order-status';
import { OrderLabel } from '@modules/common/components/order-label';
import { OrderStatusLabel } from '@modules/common/components/order-status-label';
import { PaymentStatusLabel } from '@modules/common/components/payment-status-label';

const TableHeader = () => (
  <Table.Header>
    <Table.Row className="text-black-10 text-base font-normal">
      <Table.Cell>單號</Table.Cell>
      <Table.Cell>訂單成立日期</Table.Cell>
      <Table.Cell>訂單總金額</Table.Cell>
      <Table.Cell className="text-center">訂單狀態</Table.Cell>
      <Table.Cell className="text-center">出貨狀態</Table.Cell>
      <Table.Cell className="text-center">付款狀態</Table.Cell>
    </Table.Row>
  </Table.Header>
);

const OrderLabelFulfillmentStatusTransform = (status: string) => {
  const config = fulfillmentStatusMap[status];
  if (!config) return '-';
  const { style, description } = config;
  return <OrderLabel kind={style}>{description}</OrderLabel>;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}/${month}/${day} ${hours}:${minutes}`;
};

const OrderOverview = () => {
  const navigate = useNavigate();
  const { countryCode } = useParams<{ countryCode: string }>();
  const code = countryCode || DEFAULT_COUNTRY_CODE;
  const { data } = useSuspenseQuery(ordersQueryOptions({ limit: 100 }));

  const orders = data?.orders ?? [];

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="w-full" data-testid="orders-page-wrapper">
      <div className="mb-8 flex items-center gap-y-4">
        <h1 className="text-black-30 text-3xl font-bold">訂單列表</h1>
      </div>
      {orders?.length ? (
        <div className="flex flex-col">
          <div className="border-b border-gray-200 pb-6 last:border-none last:pb-0">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader />
              <Table.Body>
                {orders.map((order) => (
                  <Table.Row
                    key={order.id}
                    className="cursor-pointer"
                    data-testid={`order-row-${order.id}`}
                    onClick={() => {
                      // Validate order.id format to prevent URL injection (order_xxxxx pattern)
                      // Design Decision: 驗證失敗時靜默忽略，不加 Sentry logging。
                      // order.id 來自 Medusa API 回傳，正常情況下一定符合格式；
                      // 不符合時僅代表資料異常，靜默阻擋導航即為足夠的防禦。
                      if (/^order_[a-zA-Z0-9]+$/.test(order.id)) {
                        navigate(`/${code}/account/orders/details/${order.id}`);
                      }
                    }}
                  >
                    <Table.Cell
                      className="text-black-30 w-28 font-bold"
                      data-testid={`order-row-number-${order.id}`}
                    >
                      {(order.metadata?.order_number ||
                        order.cart?.metadata?.order_number) as string}
                    </Table.Cell>
                    <Table.Cell
                      className="text-black-30 w-60"
                      data-testid={`order-row-created-at-${order.id}`}
                    >
                      {formatDate(String(order.created_at))}
                    </Table.Cell>
                    <Table.Cell
                      className="text-black-30 w-60"
                      data-testid={`order-row-total-${order.id}`}
                    >
                      NT$ {order.total}
                    </Table.Cell>
                    <Table.Cell
                      className="w-24 text-center text-sm"
                      data-testid={`order-row-status-${order.id}`}
                    >
                      <OrderStatusLabel order={order} />
                    </Table.Cell>
                    <Table.Cell className="w-24 text-center text-sm">
                      {order.fulfillment_status
                        ? OrderLabelFulfillmentStatusTransform(
                            order.fulfillment_status
                          )
                        : '-'}
                    </Table.Cell>
                    <Table.Cell className="w-24 text-center text-sm">
                      <PaymentStatusLabel order={order} />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="flex w-full flex-col items-center gap-y-4"
          data-testid="no-orders-container"
        >
          <p className="text-black-10 text-base font-normal">
            目前尚未有訂單紀錄
          </p>
          <div className="mt-6">
            <Button
              className="text-white-10 shadow-buttons-neutral rounded bg-red-50 px-4 py-2 font-bold"
              onClick={handleBackToHome}
            >
              回首頁逛逛
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderOverview;
